import { BRANDING_TOKENS, BRANDING_TOKEN_MAP, } from "./tokens.js";
import { FACTORY_BRANDING } from "./defaults.js";
import { alpha, bestTextOn, darken, ensureContrast, isHexColor, mix } from "./contrast.js";
/**
 * ══════════════════════════════════════════════════════════════
 *  THE RULE  (requirement #5, D-02)
 *
 *      value = org.branding[key]  ??  platform.branding[key]
 *
 *  `null` means "inherit". It never means "blank".
 *  An org's overrides live in that org's own document, so writing
 *  to one organization cannot alter another, or the platform.
 * ══════════════════════════════════════════════════════════════
 */
const isSet = (v) => v !== null && v !== undefined && v !== "";
/** Platform defaults: whatever the super admin saved, falling back to factory. */
export function resolvePlatformBranding(stored) {
    const out = {};
    for (const token of BRANDING_TOKENS) {
        const saved = stored?.[token.key];
        out[token.key] = isSet(saved) ? saved : FACTORY_BRANDING[token.key];
    }
    return out;
}
/** An organization's effective theme. This is the function requirement #5 rests on. */
export function resolveOrgBranding(platform, orgOverrides) {
    const out = {};
    for (const token of BRANDING_TOKENS) {
        const override = orgOverrides?.[token.key];
        out[token.key] = isSet(override) ? override : platform[token.key];
    }
    return out;
}
/** Convenience: raw platform document + raw org document -> effective theme. */
export function resolveBranding(platformStored, orgStored) {
    return resolveOrgBranding(resolvePlatformBranding(platformStored), orgStored);
}
export function deriveTokens(branding) {
    const c1 = isHexColor(branding.color1 ?? "") ? branding.color1 : "#C54E1B";
    const c2 = isHexColor(branding.color2 ?? "") ? branding.color2 : "#FAFAFA";
    const ink = bestTextOn(c2);
    /*
     * The sign-in panel. An organization may set `colorPanel` outright; when it
     * does not, we sink the brand colour almost all the way to a near-black,
     * keeping a trace of its hue so the panel still reads as *their* brand
     * rather than a generic dark rectangle.
     */
    const panel = isHexColor(branding.colorPanel ?? "")
        ? branding.colorPanel
        : mix(c1, "#0D1117", 0.86);
    const panelText = bestTextOn(panel);
    return {
        panel,
        panelText,
        panelMuted: mix(panel, panelText, 0.62),
        color1OnPanel: ensureContrast(c1, panel, 3),
        color1Hover: darken(c1, 0.08),
        color1Active: darken(c1, 0.14),
        color1Light: mix(c1, "#FFFFFF", 0.92),
        color1Border: mix(c1, "#FFFFFF", 0.72),
        color1Text: bestTextOn(c1),
        // Guaranteed readable as text on the page surface, whatever brand color
        // the organization chose. The fill keeps their exact color.
        color1Ink: ensureContrast(c1, c2),
        color1Ring: alpha(c1, 0.35),
        ink,
        rule: mix(c2, ink, 0.12),
        surfaceRaised: mix(c2, ink === "#1C1714" ? "#FFFFFF" : "#000000", 0.35),
    };
}
/* ──────────────────────────────────────────────────────────────
 *  Enum tokens -> concrete CSS values
 * ────────────────────────────────────────────────────────────── */
const SHADOW_CSS = {
    none: "none",
    soft: "0 1px 2px rgb(0 0 0 / 0.04), 0 2px 8px rgb(0 0 0 / 0.06)",
    medium: "0 2px 4px rgb(0 0 0 / 0.06), 0 8px 20px rgb(0 0 0 / 0.10)",
    strong: "0 4px 8px rgb(0 0 0 / 0.08), 0 16px 40px rgb(0 0 0 / 0.16)",
};
const SPACING_CSS = {
    compact: "0.875",
    normal: "1",
    roomy: "1.15",
};
const CONTAINER_CSS = {
    narrow: "960px",
    normal: "1200px",
    wide: "1440px",
    full: "100%",
};
const BORDER_WIDTH_CSS = {
    hairline: "1px",
    solid: "2px",
    none: "0px",
};
/**
 * Every CSS custom property the app reads. The client sets these on :root and
 * the whole interface repaints — no component does per-org work.
 */
export function toCssVars(branding) {
    const vars = {};
    for (const token of BRANDING_TOKENS) {
        if (!token.cssVar)
            continue;
        const value = branding[token.key];
        if (isSet(value))
            vars[token.cssVar] = value;
    }
    const d = deriveTokens(branding);
    vars["--gq-color-1-hover"] = d.color1Hover;
    vars["--gq-color-1-active"] = d.color1Active;
    vars["--gq-color-1-light"] = d.color1Light;
    vars["--gq-color-1-border"] = d.color1Border;
    vars["--gq-color-1-text"] = d.color1Text;
    vars["--gq-color-1-ink"] = d.color1Ink;
    vars["--gq-color-1-ring"] = d.color1Ring;
    vars["--gq-ink"] = d.ink;
    vars["--gq-rule"] = d.rule;
    vars["--gq-surface-raised"] = d.surfaceRaised;
    vars["--gq-panel"] = d.panel;
    vars["--gq-panel-text"] = d.panelText;
    vars["--gq-panel-muted"] = d.panelMuted;
    vars["--gq-color-1-on-panel"] = d.color1OnPanel;
    vars["--gq-shadow"] = SHADOW_CSS[branding.shadowLevel ?? "soft"] ?? SHADOW_CSS.soft;
    vars["--gq-space-scale"] = SPACING_CSS[branding.spacingScale ?? "normal"] ?? "1";
    vars["--gq-container"] = CONTAINER_CSS[branding.containerWidth ?? "wide"] ?? "1440px";
    vars["--gq-border-width"] = BORDER_WIDTH_CSS[branding.borderStyle ?? "hairline"] ?? "1px";
    // Font stacks always carry a real fallback.
    vars["--gq-font-title"] = `"${branding.fontTitle ?? "Poppins"}", "Poppins", ui-sans-serif, system-ui, sans-serif`;
    vars["--gq-font-body"] = `"${branding.fontBody ?? "Outfit"}", "Outfit", ui-sans-serif, system-ui, sans-serif`;
    return vars;
}
/** The Google Fonts URL for this theme's two faces. */
export function googleFontsUrl(branding) {
    const families = [...new Set([branding.fontTitle, branding.fontBody])].filter((f) => typeof f === "string" && f.length > 0);
    const params = families
        .map((f) => `family=${encodeURIComponent(f).replace(/%20/g, "+")}:wght@400;500;600;700`)
        .join("&");
    return `https://fonts.googleapis.com/css2?${params}&display=swap`;
}
export function toBrandingPayload(branding, orgSlug, orgName, platformAppName, 
/** Defaults to true: with no org in play, the platform's own logo is its own. */
hasOwnLogo = true) {
    return {
        orgSlug,
        orgName,
        branding,
        platformAppName: platformAppName ?? branding.appName,
        hasOwnLogo,
        cssVars: toCssVars(branding),
        fontsUrl: googleFontsUrl(branding),
    };
}
/**
 * Two letters from a name, for the mark shown when there is no logo.
 * "Zenith Pro Adhesives" -> "ZP". Matches the account avatar's fallback.
 */
export function brandInitials(name) {
    const words = (name ?? "").split(/\s+/).filter(Boolean);
    if (words.length === 0)
        return "G";
    return words
        .slice(0, 2)
        .map((w) => w.charAt(0).toUpperCase())
        .join("");
}
/** Token definition lookup, re-exported for the settings UI. */
export function tokenDef(key) {
    return BRANDING_TOKEN_MAP[key];
}
//# sourceMappingURL=resolve.js.map